import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ThemeService } from "src/app/services/theme/theme.service";
import { LanguageService } from "src/app/services/language/language.service";
import { UserService } from "../../services/user.service";
import { MatDialog } from '@angular/material/dialog';
import { ProfilePhotoDialogComponent } from './profile-photo-dialog.component';
import { FrontendLoggerService } from '../../services/frontend-logger.service';

@Component({
    selector: "app-profile",
    templateUrl: "./profile.component.html",
    styleUrls: [
      "./profile.component.layout-and-form.scss",
      "./profile.component.language-and-responsive.scss"
    ],
    standalone: false
})
export class ProfileComponent implements OnInit {
  profileForm: FormGroup;
  userProfile: any = null;
  isLoading = false;
  successMessage = "";
  errorMessage = "";
  uploadingPhoto = false;
  timezones: { value: string; label: string; offsetMinutes: number }[] = [];
  private allTimezones: { value: string; label: string; offsetMinutes: number }[] = [];
  timezoneQuery = '';

  organizationTypes = [
    { value: "enterprise", label: "wizard.org_enterprise" },
    { value: "education", label: "wizard.org_education" },
    { value: "government", label: "wizard.org_government" },
    { value: "nonprofit", label: "wizard.org_nonprofit" },
    { value: "other", label: "wizard.org_other" },
  ];

  organizationSizes = [
    { value: "small", label: "wizard.size_small" },
    { value: "medium", label: "wizard.size_medium" },
    { value: "large", label: "wizard.size_large" },
    { value: "enterprise", label: "wizard.size_enterprise" },
  ];

  countries = [
    "Argentina","Bolivia","Brasil","Canadá","Chile","Colombia","Costa Rica","Cuba",
    "Ecuador","El Salvador","España","Estados Unidos","Francia","Guatemala","Honduras",
    "México","Nicaragua","Panamá","Paraguay","Perú","Portugal","Puerto Rico",
    "República Dominicana","Uruguay","Venezuela","Alemania","Italia","Reino Unido",
    "China","Japón","India","Australia","Otro",
  ];

  constructor(
    private fb: FormBuilder, 
    private userService: UserService,
    public themeService: ThemeService,
    public languageService: LanguageService,
    private dialog: MatDialog,
    private frontendLogger: FrontendLoggerService
  ) {
    this.userService = userService;
    this.profileForm = this.fb.group({
      name: ["", Validators.required],
      email: [{ value: "", disabled: true }],
      company: [""],
      role: [{ value: "", disabled: true }],
      jobTitle: [""],
      organizationType: [""],
      organizationSize: [""],
      country: [""],
      language: [""],
      timezone: [""],
    });
  }

  ngOnInit(): void {
    // Construir lista de timezones (similar a wizard)
    try {
      let tzRaw: string[] = [];
      try { tzRaw = (Intl as any).supportedValuesOf('timeZone'); } catch { tzRaw = []; }
      if (Array.isArray(tzRaw) && tzRaw.length) {
        const now = new Date();
        const buildOffset = (tz: string): { offsetStr: string; offsetMinutes: number } => {
          try {
            const dtf = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset', hour: '2-digit', minute: '2-digit' });
            const parts = dtf.formatToParts(now);
            const tzName = parts.find(p => p.type === 'timeZoneName')?.value || '';
            const m = tzName.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
            if (m) {
              const h = m[1];
              const mm = m[2] ? m[2] : '00';
              const hoursNum = parseInt(h, 10);
              const totalMinutes = hoursNum * 60 + (hoursNum >= 0 ? 1 : -1) * (parseInt(mm, 10));
              const sign = hoursNum >= 0 ? '+' : '-';
              const absH = String(Math.abs(hoursNum)).padStart(2, '0');
              return { offsetStr: `UTC${sign}${absH}:${mm}`, offsetMinutes: totalMinutes };
            }
          } catch {}
          try {
            const utc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
            const tzDate = new Date(now.toLocaleString('en-US', { timeZone: tz }));
            const diffMinutes = Math.round((tzDate.getTime() - utc) / 60000);
            const sign = diffMinutes >= 0 ? '+' : '-';
            const abs = Math.abs(diffMinutes);
            const hh = String(Math.floor(abs / 60)).padStart(2, '0');
            const mm = String(abs % 60).padStart(2, '0');
            return { offsetStr: `UTC${sign}${hh}:${mm}`, offsetMinutes: diffMinutes };
          } catch { return { offsetStr: 'UTC+00:00', offsetMinutes: 0 }; }
        };
        this.timezones = tzRaw.map(z => {
          const { offsetStr, offsetMinutes } = buildOffset(z);
            return { value: z, label: `(${offsetStr}) ${z}`, offsetMinutes };
        }).sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));
        this.allTimezones = [...this.timezones];
      } else {
        const fallback = ['UTC','Europe/Madrid','America/New_York','America/Mexico_City','America/Bogota'];
        this.timezones = fallback.map(z => ({ value: z, label: `(UTC) ${z}`, offsetMinutes: 0 }));
        this.allTimezones = [...this.timezones];
      }
    } catch {
      const fallback = ['UTC','Europe/Madrid','America/New_York','America/Mexico_City','America/Bogota'];
      this.timezones = fallback.map(z => ({ value: z, label: `(UTC) ${z}`, offsetMinutes: 0 }));
      this.allTimezones = [...this.timezones];
    }
    // Cargar perfil del usuario y ruta
    this.loadUserProfile();
    localStorage.setItem('lastRoute', '/profile');
  }

  loadUserProfile(): void {
    this.isLoading = true;
    this.userService.getCurrentUser().subscribe({
      next: (profile) => {
        this.userProfile = profile;
        // Normalizar language a minúsculas si backend devuelve 'ES', 'EN', etc.
        let rawLang = (profile as any)["language"] || (profile as any)["Language"] || '';
        if (typeof rawLang === 'string') {
          rawLang = rawLang.toLowerCase();
          if (!['es','en','pt','fr','ja'].includes(rawLang)) {
            rawLang = 'es'; // fallback seguro
          }
          // Aplicar inmediatamente al servicio si difiere
          if (rawLang && this.languageService.getCurrentLang() !== rawLang) {
            this.languageService.setLanguage(rawLang as any);
          }
        }
        const tzValue = (profile as any)["timeZone"] || (profile as any)["timezone"] || "";
        this.profileForm.patchValue({
          name: profile?.name || "",
          email: profile?.email || "",
          company: profile?.company || "",
          role: profile?.role || "",
          jobTitle: (profile as any)["jobTitle"] || "",
          organizationType: (profile as any)["organizationType"] || "",
          organizationSize: (profile as any)["organizationSize"] || "",
          country: (profile as any)["country"] || (profile as any)["Country"] || "",
          language: rawLang,
          timezone: tzValue,
        });
        this.isLoading = false;
      },
      error: (error) => {
        this.frontendLogger.error(`[ProfileComponent] Error al cargar perfil de usuario: ${JSON.stringify(error)}`);
        this.errorMessage = this.translate("profile.load_error");
        this.isLoading = false;
      },
    });
  }

  saveProfile(): void {
    if (this.profileForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.successMessage = "";
    this.errorMessage = "";

    const formData = {
      name: this.profileForm.get("name")?.value,
      company: this.profileForm.get("company")?.value,
      jobTitle: this.profileForm.get("jobTitle")?.value,
      organizationType: this.profileForm.get("organizationType")?.value,
      organizationSize: this.profileForm.get("organizationSize")?.value,
      country: this.profileForm.get("country")?.value,
      language: this.profileForm.get("language")?.value,
      timeZone: this.profileForm.get("timezone")?.value,
    };

    this.userService.saveProfile(formData).subscribe({
      next: () => {
        this.isLoading = false;
        this.successMessage = this.translate("profile.save_success");
        // Aplicar cambios inmediatos
        if (formData.language) {
          this.languageService.setLanguage(formData.language);
        }
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      },
      error: (error) => {
        this.frontendLogger.error(`[ProfileComponent] Error al guardar perfil: ${JSON.stringify(error)}`);
        this.isLoading = false;
        this.errorMessage = this.translate("profile.save_error");
      },
    });
  }

  onAvatarClick(): void {
    const dialogRef = this.dialog.open(ProfilePhotoDialogComponent, {
      data: { currentPhoto: this.userProfile?.urlPhoto || 'assets/images/default-avatar.png' },
      width: '350px',
      autoFocus: false
    });
    dialogRef.afterClosed().subscribe((file: File) => {
      if (file) {
        this.uploadingPhoto = true;
        this.userService.uploadProfilePhoto(file).subscribe({
          next: (res) => {
            if (res && res.url) {
              this.userProfile.urlPhoto = res.url;
              this.errorMessage = ""; // Limpiar mensaje de error si el upload fue exitoso
            }
            this.uploadingPhoto = false;
          },
          error: (err) => {
            this.uploadingPhoto = false;
            // Manejo específico para error de tamaño de archivo
            if (
              err?.status === 400 &&
              err?.error?.errors &&
              Array.isArray(err.error.errors[""]) &&
              err.error.errors[""].some((msg: string) =>
                msg.includes("Request body too large")
              )
            ) {
              this.errorMessage = "La imagen es demasiado grande. El tamaño máximo permitido es 1 MB.";
            } else {
              this.errorMessage = this.translate('profile.photo_upload_error');
            }
          }
        });
      }
    });
  }

  get hasPhoto(): boolean {
    const p = this.userProfile?.urlPhoto;
    return !!p && !/default-avatar/i.test(p);
  }

  get initials(): string {
    const name = (this.userProfile?.name || "").trim();
    if (!name) return "?";
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) || "";
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
    return ((first + last).toUpperCase()) || "?";
  }

  getRoleLabel(role: string): string {
    switch (role) {
      case "admin":
        return this.translate("role.admin");
      case "executive":
        return this.translate("role.executive");
      case "user":
        return this.translate("role.user");
      default:
        return role;
    }
  }

  translate(key: string): string {
    return this.languageService.translate(key);
  }

  onTimezoneQueryChange(value: string): void {
    this.timezoneQuery = value;
    const q = value.trim().toLowerCase();
    if (!q) { this.timezones = [...this.allTimezones]; return; }
    this.timezones = this.allTimezones.filter(t => t.value.toLowerCase().includes(q) || t.label.toLowerCase().includes(q));
    if (this.timezones.length === 1) {
      // Autoseleccionar único resultado
      this.profileForm.patchValue({ timezone: this.timezones[0].value });
    }
  }

  onTimezoneEnter(): void {
    if (this.timezones.length === 1) {
      this.profileForm.patchValue({ timezone: this.timezones[0].value });
    }
  }
}
